import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminMode } from '../context/AdminModeContext';

const AdminSignIn = () => {
  const navigate = useNavigate();
  const { enableAdminMode } = useAdminMode();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });
  const [submitError, setSubmitError] = useState('');

  const emailError = useMemo(() => {
    if (!touched.email) return '';
    const trimmed = email.trim();
    if (!trimmed) return 'Email is required.';
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!ok) return 'Enter a valid email address.';
    return '';
  }, [email, touched.email]);

  const passwordError = useMemo(() => {
    if (!touched.password) return '';
    if (!password) return 'Password is required.';
    if (password.length < 6) return 'Password must be at least 6 characters.';
    return '';
  }, [password, touched.password]);

  const canSubmit = !emailError && !passwordError && email.trim() && password;

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitError('');
    setTouched({ email: true, password: true });

    if (!canSubmit) {
      setSubmitError('Please fix the highlighted fields.');
      return;
    }

    enableAdminMode();
    navigate('/home');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 shadow-[0_10px_30px_rgba(0,0,0,0.06)] rounded-2xl p-8">
          <div className="text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-black">Admin Sign In</h1>
            <p className="mt-2 text-sm text-gray-600">Restricted access for administrators only</p>
          </div>

          <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
            <div>
              <label className="block text-sm font-medium text-gray-800">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                autoComplete="email"
                placeholder="admin@ditris.com"
                className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors bg-white ${
                  emailError ? 'border-red-500 focus:border-red-600' : 'border-gray-300 focus:border-black'
                }`}
              />
              {emailError ? <p className="mt-2 text-xs text-red-600">{emailError}</p> : null}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-800">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                autoComplete="current-password"
                placeholder="••••••••"
                className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors bg-white ${
                  passwordError ? 'border-red-500 focus:border-red-600' : 'border-gray-300 focus:border-black'
                }`}
              />
              {passwordError ? <p className="mt-2 text-xs text-red-600">{passwordError}</p> : null}
            </div>

            {submitError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {submitError}
              </div>
            ) : null}

            <button
              type="submit"
              className={`w-full rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                canSubmit ? 'bg-black text-white hover:bg-gray-900' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              Sign In
            </button>

            <button
              type="button"
              onClick={() => {
                enableAdminMode();
                navigate('/home');
              }}
              className="w-full rounded-lg px-4 py-3 text-sm font-medium border border-gray-300 text-black bg-white hover:bg-gray-50 transition-colors"
            >
              Bypass Login (Dev Mode)
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="text-sm text-gray-600 hover:text-black transition-colors"
          >
            Back to website
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSignIn;
